const API_URL = process.env.REACT_APP_API_URL;

// Helper function to handle the response from fetch calls
async function handleResponse(response) {
    if (!response.ok) {
        const errorData = await response.json();
        return { error: true, message: errorData.message || 'Network error' };
    }
    return { error: false, data: await response.json() };
}

// Generic fetch function
async function fetchData(url, options = {}) {
    try {
        const response = await fetch(url, options);
        return await handleResponse(response);
    } catch (error) {
        return { error: true, message: error.message };
    }
}

export async function fetchFullSpacesData() {
  const token = localStorage.getItem('userToken');
  if (!token) return { error: true, message: 'No token found' };
  const options = { headers: { 'Authorization': `Bearer ${token}` } };
  return fetchData(`${API_URL}/users/me/fullSpacesData`, options);
}

// Fetch all users
export async function fetchUsers() {
    return fetchData(`${API_URL}/users/`);
}

// Fetch a single user
export async function fetchUser() {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No token found' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/users/me`, options);
}

// Fetch username by user ID
export async function fetchUsernameByID(userId) {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
    }
    const options = {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    return fetchData(`${API_URL}/users/usernameById?user_id=${userId}`, options);
}

export async function registerUser(user) {
    if (!user || !user.user_name || !user.email || !user.password) {
        throw new Error("Todos los campos (nombre, email, contraseña) son obligatorios.");
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(user.email)) {
        throw new Error("El formato del correo electrónico es inválido.");
    }

    try {
        const response = await fetch(`${API_URL}/users/register`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(user)
        });
        if (!response.ok) {
            throw new Error(`Failed to create user: ${response.statusText}`);
        }
        return response.json();
    } catch (error) {
        console.error("Error creating user:", error);
        throw error;
    }
}


// Update an existing user
export async function updateUser(token, user) {
    if (!user || (!user.user_name && !user.email)) {
        return { error: true, message: "Al menos uno de los campos (nombre, email) debe ser proporcionado para actualizar el usuario." };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (user.email && !emailRegex.test(user.email)) {
        return { error: true, message: "El formato del correo electrónico es inválido." };
    }

    const options = {
        method: "PUT",
        headers: {
            'Authorization': `Bearer ${token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
    };
    return fetchData(`${API_URL}/users/me`, options);
}

// Delete a user
export async function deleteUser(token) {
    const options = {
        method: "DELETE",
        headers: {
            'Authorization': `Bearer ${token}`,
        },
    };
    return fetchData(`${API_URL}/users/me`, options);
}

// Fetch spaces for a user
export async function fetchSpacesForUser() {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
    }
    const options = {
        headers: { 'Authorization': `Bearer ${token}` }
    };
    return fetchData(`${API_URL}/users/me/spaces`, options);
}

// Fetch free hubs for a user
export async function fetchFreeHubsForUser() {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
    }
    const options = {
        headers: { 'Authorization': `Bearer ${token}` }
    };
    return fetchData(`${API_URL}/users/me/freeHubs`, options);
}

// Fetch custom plants for a user
export async function fetchCustomPlantsForUser() {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
    }
    const options = {
        headers: { 'Authorization': `Bearer ${token}` }
    };
    return fetchData(`${API_URL}/users/me/customPlants`, options);
}

// Fetch pots for a user
export async function fetchPotsForUser() {
    const token = localStorage.getItem('userToken');
    if (!token) {
        return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
    }
    const options = {
        headers: { 'Authorization': `Bearer ${token}` }
    };
    return fetchData(`${API_URL}/users/me/pots`, options);
}

// User login and logout management
export async function loginUser(email, password, setError, navigate) {
    if (!email || !password) {
        setError("Email y contraseña son obligatorios para el inicio de sesión.");
        return { error: true };
    }

    const loginDetails = new URLSearchParams();
    loginDetails.append('email', email);
    loginDetails.append('password', password);

    const options = {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "application/json"
        },
        body: loginDetails
    };
    try {
        const response = await fetch(`${API_URL}/auth/login`, options);
        if (!response.ok) {
            const errorData = await response.json();
            setError(errorData.message || 'Error de inicio de sesión');
            return { error: true };
        }

        const data = await response.json();
        localStorage.setItem('userToken', data.access_token);
        const expirationDate = new Date(new Date().getTime() + 7200 * 1000);
        localStorage.setItem('tokenExpirationDate', expirationDate.toISOString());
        navigate('/home');
        return { error: false, data };
    } catch (error) {
        setError(error.message);
        return { error: true };
    }
}

export async function logoutUser(navigate) {
    localStorage.removeItem('userToken');
    localStorage.removeItem('tokenExpirationDate');
    navigate('/login');
    return { error: false };
}

// Fetch the authenticated user's timezone info (IANA + label)
export async function fetchUserTimezoneInfo() {
  const token = localStorage.getItem('userToken');
  if (!token) {
    return { error: true, message: 'No se encontró un token de autenticación. Por favor inicia sesión.' };
  }
  const options = {
    headers: { 'Authorization': `Bearer ${token}` }
  };
  return fetchData(`${API_URL}/users/me/timezoneInfo`, options);
}
